import { useState, MouseEvent } from 'react';

import { Popover } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';

import {
  List,
  ListItem,
  ListItemText,
  ListSearchInput,
  Scroller,
  Icons,
  Skeleton,
  Tooltip,
  tokens,
  Typography,
  Box,
} from '@cast/design-system';
import { useNavigate } from '@cast/infrastructure-organization';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { useNodesQuery } from '@cast/split-me/hooks/queries/nodes';

import { NavTriggerCaret } from '../NavTriggerCaret';
import {
  getNavTriggerButtonSx,
  navTriggerGap,
  navTriggerLabelSx,
  navTriggerPaddingX,
} from '../navTriggerStyles';

/** Node id lives on nested Inventory Routes, not the AppLayout match — parse from URL. */
const getNodeIdFromPathname = (pathname: string): string | undefined => {
  const match = pathname.match(/\/inventory\/nodes\/([^/]+)/);
  return match?.[1];
};

export const NodeSelect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clusterId } = useParams<{ clusterId: string }>();
  const nodeId = getNodeIdFromPathname(location.pathname);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isPopoverOpen = Boolean(anchorEl);
  const [searchInputValue, setSearchInputValue] = useState('');
  const menuWidth = anchorEl?.getBoundingClientRect().width;

  const { nodes, isLoading, error, refetch } = useNodesQuery({
    clusterId: clusterId!,
  });

  const currentNode = nodes?.find((node) => node.id === nodeId);
  const filteredNodes = (nodes || []).filter((node) => {
    const query = searchInputValue.toLowerCase();
    if (!query) {
      return true;
    }
    return (
      node.name?.toLowerCase().includes(query) ||
      node.id.toLowerCase().includes(query) ||
      node.instanceType?.toLowerCase().includes(query)
    );
  });

  const handleOpen = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const closePopover = () => {
    setAnchorEl(null);
    setSearchInputValue('');
  };

  const handleNodeSelect = (selectedNodeId: string) => {
    closePopover();
    if (!nodeId || selectedNodeId === nodeId) {
      return;
    }
    navigate(
      location.pathname.replace(
        `/inventory/nodes/${nodeId}`,
        `/inventory/nodes/${selectedNodeId}`
      )
    );
  };

  const triggerLabel = currentNode?.name || nodeId || 'Select node';

  return (
    <>
      <Box
        component="button"
        type="button"
        alignItems="center"
        px={navTriggerPaddingX}
        onClick={handleOpen}
        disabled={isLoading}
        display="flex"
        gap={navTriggerGap}
        sx={getNavTriggerButtonSx(isPopoverOpen)}
        data-testid="node-chooser-trigger"
      >
        {isLoading ? (
          <Box
            sx={{
              ...navTriggerLabelSx,
              width: 140,
              maxWidth: 220,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Skeleton variant="rounded" width={140} height={16} />
          </Box>
        ) : (
          <Tooltip title={triggerLabel} wrapIf={triggerLabel.length > 28}>
            <Typography
              variant="P14B"
              noWrap
              sx={{
                ...navTriggerLabelSx,
                color: tokens.theme.fg.action.onGhost,
                maxWidth: 220,
              }}
            >
              {triggerLabel}
            </Typography>
          </Tooltip>
        )}
        <NavTriggerCaret />
      </Box>

      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ marginTop: '4px', padding: '4px 0px' }}
        slotProps={{
          paper: {
            sx: { backgroundColor: tokens.theme.bg.structural.surface },
          },
        }}
        disableScrollLock
      >
        <Box minWidth={270} width={menuWidth} data-testid="node-chooser-menu">
          <ListSearchInput
            autoFocus
            autoComplete="off"
            size="small"
            placeholder="Enter node name"
            listItemProps={{
              sx: { '&.Mui-focusVisible': { background: 'none' } },
            }}
            value={searchInputValue}
            onChange={(_: unknown, v: string) => setSearchInputValue(v)}
            showClearButton
            clearButtonProps={{
              onClick: () => setSearchInputValue(''),
            }}
            disabled={isLoading || !!error || !nodes?.length}
          />
          <List>
            {isLoading ? (
              <Box px={12} py={8}>
                <Skeleton variant="rounded" height={32} />
                <Skeleton variant="rounded" height={32} sx={{ mt: 1 }} />
              </Box>
            ) : error ? (
              <Box px={12} py={16}>
                <FailedToLoad
                  title="Failed to load nodes"
                  refresh={refetch}
                  compact
                />
              </Box>
            ) : !filteredNodes.length ? (
              <ListItem
                startAdornment={<Icons.Nut size={14} />}
                startAdornmentSx={{
                  alignSelf: 'flex-start',
                  marginTop: '2px',
                }}
                multiline
              >
                <ListItemText
                  primary="No nodes found"
                  secondary="Adjust your search criteria"
                />
              </ListItem>
            ) : (
              <Scroller
                sx={{ gap: '2px', overflow: 'auto', maxHeight: '225px' }}
              >
                {filteredNodes.map((node) => (
                  <ListItem
                    key={node.id}
                    onClick={() => handleNodeSelect(node.id)}
                    selected={node.id === nodeId}
                    multiline
                  >
                    <ListItemText
                      primary={node.name || node.id}
                      primaryTypographyProps={{ variant: 'P12M', noWrap: true }}
                      secondary={node.instanceType}
                    />
                  </ListItem>
                ))}
              </Scroller>
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
};
